export function formatFinanceLabel(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function groupDigits(value: string): string {
  return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Exact currency formatter for API Decimal(18,2) strings and BigInt cents.
 * It never routes persisted money through a binary floating-point number.
 */
export function formatFinanceMoney(value: string | number | bigint | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  let raw: string;
  if (typeof value === 'bigint') {
    const negative = value < 0n;
    const absolute = negative ? -value : value;
    const cents = absolute.toString().padStart(3, '0');
    raw = `${negative ? '-' : ''}${cents.slice(0, -2)}.${cents.slice(-2)}`;
  } else if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '—';
    raw = value.toFixed(2);
  } else {
    raw = value;
  }
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(raw);
  if (!match) return '—';
  const [, sign, integer, fraction = ''] = match;
  return `${sign ? '-' : ''}$${groupDigits(integer)}.${fraction.padEnd(2, '0')}`;
}

export function moneyToCents(value: string | null | undefined): bigint {
  const amount = measuredMoneyToCents(value);
  if (amount === null) throw new Error('A required financial amount is unavailable.');
  return amount;
}

export function measuredMoneyToCents(value: string | null | undefined): bigint | null {
  if (value === null || value === undefined) return null;
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(value);
  if (!match) return null;
  const amount = BigInt(match[2]) * 100n + BigInt((match[3] ?? '').padEnd(2, '0') || '0');
  return match[1] ? -amount : amount;
}

export function formatFinanceDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
}

export function financeStatusVariant(
  value: string,
): 'default' | 'outline' | 'success' | 'warning' | 'critical' {
  if (['ACTIVE', 'APPROVED', 'POSTED', 'INCLUDED'].includes(value)) return 'success';
  if (['SUBMITTED', 'OPEN', 'DRAFT'].includes(value)) return 'warning';
  if (['VOIDED', 'REVERSED', 'EXCLUDED', 'ARCHIVED'].includes(value)) return 'critical';
  return 'outline';
}
